// /garage/new and /garage/:id — create / edit one aircraft.

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Section, Cell, List, Button, Input, Textarea, Caption, Spinner, Banner,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const CLASSES = [
  { code: "F3A", label: "✈️ Самолёт", note: "Радиоуправляемые самолёты" },
  { code: "F9U", label: "🚁 Дрон / коптер", note: "Мультироторы и FPV" },
];

const WEIGHT_CLASSES = [
  { code: "under_250g", label: "< 250 г", note: "Регистрация в Росавиации не требуется" },
  { code: "250g_4kg",   label: "250 г – 4 кг", note: "Большинство хобби-моделей" },
  { code: "4_25kg",     label: "4 – 25 кг",     note: "Тяжёлые F3A, FPV-Wing" },
  { code: "over_25kg",  label: "> 25 кг",       note: "Спортивные и технологические" },
];

export default function AircraftEdit() {
  const { id } = useParams();
  const isNew = id === "new" || id === undefined;
  const navigate = useNavigate();
  usePageBack("/garage");

  const [form, setForm] = useState({
    name: "", class_code: "F3A", weight_class: "",
    manufacturer: "", model: "", build_no: "", serial_no: "",
    registration_no: "", notes: "", is_primary: false,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api(`/api/v1/me/aircraft`)
      .then((r) => {
        const found = (r.aircraft || []).find((a) => String(a.id) === String(id));
        if (!found) { setError("Модель не найдена"); return; }
        setForm({
          name: found.name || "",
          class_code: found.class_code || "F3A",
          weight_class: found.weight_class || "",
          manufacturer: found.manufacturer || "",
          model: found.model || "",
          build_no: found.build_no || "",
          serial_no: found.serial_no || "",
          registration_no: found.registration_no || "",
          notes: found.notes || "",
          is_primary: !!found.is_primary,
        });
        setLoading(false);
      })
      .catch((e) => setError(`Не удалось загрузить: ${e.message}`));
  }, [id, isNew]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    if (!form.name.trim()) { setError("Название обязательно."); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        weight_class: form.weight_class || null,
      };
      if (isNew) {
        await api("/api/v1/me/aircraft", { method: "POST", body: JSON.stringify(payload) });
      } else {
        await api(`/api/v1/me/aircraft/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
      }
      navigate("/garage");
    } catch (e) {
      setError(e.message || "Не удалось сохранить.");
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Удалить модель «${form.name}»?`)) return;
    setSaving(true);
    try {
      await api(`/api/v1/me/aircraft/${id}`, { method: "DELETE" });
      navigate("/garage");
    } catch (e) {
      setError(e.message || "Не удалось удалить.");
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <List>
        <Section><Cell before={<Spinner size="m" />}>Загружаем…</Cell></Section>
      </List>
    );
  }

  return (
    <List>
      {error && <Banner type="section" header="Ошибка">{error}</Banner>}

      <Section header={isNew ? "Новая модель" : "Редактирование модели"}>
        <Cell>
          <Input
            header="Название (позывной)"
            placeholder="например, Yak-54 #2"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Cell>
      </Section>

      <Section header="Класс">
        {CLASSES.map((c) => (
          <Cell
            key={c.code}
            onClick={() => set("class_code", c.code)}
            after={form.class_code === c.code ? "✓" : ""}
            subtitle={c.note}
          >
            {c.label}
          </Cell>
        ))}
      </Section>

      <Section header="Весовой класс">
        {WEIGHT_CLASSES.map((w) => (
          <Cell
            key={w.code}
            onClick={() => set("weight_class", form.weight_class === w.code ? "" : w.code)}
            after={form.weight_class === w.code ? "✓" : ""}
            subtitle={w.note}
          >
            {w.label}
          </Cell>
        ))}
      </Section>

      <Section header="Дополнительно (необязательно)">
        <Cell><Input header="Производитель" placeholder="например, Tomahawk" value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} /></Cell>
        <Cell><Input header="Модель"        placeholder="например, Yak-54"  value={form.model}        onChange={(e) => set("model", e.target.value)} /></Cell>
        <Cell><Input header="Номер сборки"  placeholder="#2"                 value={form.build_no}     onChange={(e) => set("build_no", e.target.value)} /></Cell>
        <Cell><Input header="Серийный номер" placeholder=""                  value={form.serial_no}    onChange={(e) => set("serial_no", e.target.value)} /></Cell>
        <Cell><Input header="Учётный номер БВС (Росавиация)" placeholder="" value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} /></Cell>
        <Cell><Textarea header="Заметки" placeholder="" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Cell>
      </Section>

      <Section header="Основная модель">
        <Cell
          onClick={() => set("is_primary", !form.is_primary)}
          after={form.is_primary ? "★" : ""}
          subtitle="Основная подставляется по умолчанию в мастере бронирования."
        >
          {form.is_primary ? "Назначена основной" : "Сделать основной"}
        </Cell>
      </Section>

      <Section>
        <Cell>
          <Button stretched mode="filled" disabled={saving} onClick={save}>
            {saving ? "Сохраняем…" : isNew ? "Добавить" : "Сохранить"}
          </Button>
        </Cell>
        {!isNew && (
          <Cell>
            <Button stretched mode="plain" disabled={saving} onClick={remove}>
              🗑 Удалить модель
            </Button>
          </Cell>
        )}
        <Cell>
          <Button stretched mode="plain" disabled={saving} onClick={() => navigate("/garage")}>
            ← Отмена
          </Button>
        </Cell>
      </Section>

      <Section>
        <Cell>
          <Caption level="1">
            Учётный номер БВС обязателен для моделей тяжелее 250 г (Росавиация). Сохраняем как текст без проверки — у нас нет API для валидации.
          </Caption>
        </Cell>
      </Section>
    </List>
  );
}
